import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState, AppThunk, store } from "../../app/store";
import {
  logoutServer,
  redeemEnvHandoffServer,
  refreshTokenSignInServer,
  respondToSignInServer,
  signInServer,
  verifySoftwareTokenServer,
  webauthnSignInCompleteServer,
  webauthnSignInStartServer,
} from "./accountAPI";
import {
  serializeAuthenticationCredential,
  toCredentialRequestOptions,
} from "./webauthn";
// import { getBusinessesFromServer } from "./businessesApi";
// import { fetchCount } from './counterAPI';
import { v4 as uuidv4 } from "uuid";
import { AccessLevel } from "../../interfaces/accessLevel.enum";
import { PlatformType } from "../../interfaces/paxTerminal.interface";
import { EmployeeType } from "../../interfaces/klearlyAdmin.interface";
export interface accountState {
  idToken: string;
  accessToken: string;
  status: "idle" | "loading" | "failed";
  sessionToken?: string;
  MFASetupComplete?: boolean;
  MFASetupQrcode?: string;
  MFASetupAccessToken?: string;
  accessLevel: AccessLevel[];
  isRefreshingAccess: boolean;
  user: {
    name: string;
    email: string;
    partnerId?: string;
    partnerName?: string;
    employeeType?: EmployeeType;
  };
  couplingPlatformAccess?: PlatformType;
}

const initialState: accountState = {
  idToken: "",
  accessToken: "",
  sessionToken: "",
  status: "idle",
  MFASetupComplete: false,
  MFASetupQrcode: "",
  MFASetupAccessToken: "",
  accessLevel: [],
  isRefreshingAccess: false,
  user: { name: "", email: "", partnerId: "", partnerName: "" },
};

export const signIn = createAsyncThunk(
  "account/signIn",
  async (props: { email: string; password: string }) => {
    const response = await signInServer(props);

    return response;
  }
);
export const verifySoftwareToken = createAsyncThunk(
  "account/verifySoftwareToken",
  async (props: {
    session?: string;
    accessToken?: string;
    confirmationCode: string;
  }) => {
    const response = await verifySoftwareTokenServer(props);
    return response;
  }
);
export const respondToSignIn = createAsyncThunk(
  "account/respondToSignIn",
  async (props: {
    session: string;
    confirmationCode: string;
    username: string;
  }) => {
    let deviceId = window.localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId = uuidv4();
    }

    const response = await respondToSignInServer({ ...props, deviceId });

    if ("tokens" in response) {
      window.sessionStorage.setItem(
        "refreshToken",
        response.tokens.refreshToken
      );
      if (response.tokens.accessToken)
        window.sessionStorage.setItem(
          "accessToken",
          response.tokens.accessToken
        );
      window.localStorage.setItem("deviceId", deviceId);
    }
    return response;
  }
);

export const refreshTokenSignIn = createAsyncThunk(
  "account/refreshTokenSignIn",
  async (props: { refreshToken: string }, thunkAPI) => {
    const idToken = (thunkAPI.getState() as RootState).account.idToken;
    const response = await refreshTokenSignInServer({ ...props, idToken });
    // The value we return becomes the `fulfilled` action payload

    return response;
  }
);
export const logout = createAsyncThunk(
  "account/logout",
  async (_, thunkAPI) => {
    const refreshToken = sessionStorage.getItem("refreshToken");

    if (!refreshToken) return;
    const idToken = (thunkAPI.getState() as RootState).account.idToken;
    const response = await logoutServer({ refreshToken, idToken });
    sessionStorage.removeItem("refreshToken");
    return response;
  }
);

export const redeemHandoff = createAsyncThunk(
  "account/redeemHandoff",
  async (props: { code: string }) => {
    let deviceId = window.localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId = uuidv4();
    }
    const response = await redeemEnvHandoffServer(props);
    window.sessionStorage.setItem("refreshToken", response.tokens.refreshToken);
    if (response.tokens.accessToken)
      window.sessionStorage.setItem("accessToken", response.tokens.accessToken);
    window.localStorage.setItem("deviceId", deviceId);
    return response;
  }
);

export const passkeySignIn = createAsyncThunk(
  "account/passkeySignIn",
  async (props: { email: string }) => {
    const start = await webauthnSignInStartServer({ email: props.email });
    if (!("credentialRequestOptions" in start))
      return { outcome: "unavailable" as const };

    let assertion: PublicKeyCredential | null = null;
    try {
      const options = toCredentialRequestOptions(
        start.credentialRequestOptions as {
          publicKey?: Record<string, unknown>;
        }
      );
      assertion = (await navigator.credentials.get(
        options
      )) as PublicKeyCredential | null;
    } catch (err) {
      const name = (err as Error)?.name;
      if (name === "NotAllowedError" || name === "AbortError")
        return { outcome: "dismissed" as const };
      return { outcome: "error" as const };
    }
    if (!assertion) return { outcome: "dismissed" as const };

    let deviceId = window.localStorage.getItem("deviceId");
    if (!deviceId) deviceId = uuidv4();
    let result: Awaited<ReturnType<typeof webauthnSignInCompleteServer>>;
    try {
      result = await webauthnSignInCompleteServer({
        email: props.email,
        session: start.session,
        credential: serializeAuthenticationCredential(assertion),
      });
    } catch {
      return { outcome: "error" as const };
    }
    window.sessionStorage.setItem("refreshToken", result.tokens.refreshToken);
    window.sessionStorage.setItem("accessToken", result.tokens.accessToken);
    window.localStorage.setItem("deviceId", deviceId);
    return { outcome: "success" as const, ...result };
  }
);

export const accountSlice = createSlice({
  name: "accountSlice",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setIdTokenFrom401Request: (
      state,
      action: PayloadAction<{ idToken: string }>
    ) => {
      state.idToken = action.payload.idToken;
    },
    resetUserState: (state) => {
      state.idToken = "";
      state.accessToken = "";
      sessionStorage.removeItem("accessToken");
      state.sessionToken = "";
      state.status = "idle";
      state.MFASetupComplete = false;
      state.MFASetupQrcode = "";
      state.MFASetupAccessToken = "";
      state.accessLevel = [];
      state.isRefreshingAccess = false;
      state.user = { name: "", email: "", partnerId: "" };
      state.couplingPlatformAccess = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = "loading";
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.status = "idle";
        state.sessionToken = action.payload.session;
        state.MFASetupQrcode = action.payload.qrcode;
        state.MFASetupAccessToken = action.payload.mfaSetupAccessToken;
        state.MFASetupComplete = !action.payload.qrcode;
      })
      .addCase(verifySoftwareToken.fulfilled, (state, action) => {
        state.status = "idle";
        state.MFASetupComplete = action.payload.status === "SUCCESS";
        state.sessionToken =
          action.payload.status === "SUCCESS" ? "" : action.payload.session;
      })
      .addCase(signIn.rejected, (state, action) => {
        if (action.error.message && action.error.code) state.status = "failed";
      })
      .addCase(respondToSignIn.fulfilled, (state, action) => {
        if ("tokens" in action.payload) {
          state.idToken = action.payload.tokens.idToken;
          if (action.payload.tokens.accessToken)
            state.accessToken = action.payload.tokens.accessToken;
          state.accessLevel = action.payload.accessLevel;
          state.couplingPlatformAccess = action.payload.couplingPlatformAccess;
          state.user = action.payload.user;
        }

        if ("session" in action.payload)
          state.sessionToken = action.payload.session;
        //here we also locally save the refresh token somewhere..
      })
      .addCase(respondToSignIn.rejected, (state) => {
        state.sessionToken = "";
        //here we also locally save the refresh token somewhere..
      })
      .addCase(redeemHandoff.fulfilled, (state, action) => {
        state.idToken = action.payload.tokens.idToken;
        if (action.payload.tokens.accessToken)
          state.accessToken = action.payload.tokens.accessToken;
        state.accessLevel = action.payload.accessLevel;
        state.couplingPlatformAccess = action.payload.couplingPlatformAccess;
        state.user = action.payload.user;
      })
      .addCase(passkeySignIn.fulfilled, (state, action) => {
        if (action.payload.outcome === "success") {
          state.idToken = action.payload.tokens.idToken;
          state.accessToken = action.payload.tokens.accessToken;
          state.accessLevel = action.payload.accessLevel;
          state.couplingPlatformAccess = action.payload.couplingPlatformAccess;
          state.user = action.payload.user;
        }
      })
      .addCase(refreshTokenSignIn.fulfilled, (state, action) => {
        let currrentRefreshToken = sessionStorage.getItem("refreshToken");
        sessionStorage.setItem("refreshToken", currrentRefreshToken!);
        state.idToken = action.payload.idToken;
        if (action.payload.accessToken) {
          state.accessToken = action.payload.accessToken;
          sessionStorage.setItem("accessToken", action.payload.accessToken);
        }
        state.accessLevel = action.payload.accessLevel;
        state.couplingPlatformAccess = action.payload.couplingPlatformAccess;
        state.user = action.payload.user;
        state.isRefreshingAccess = false;
      })
      .addCase(refreshTokenSignIn.pending, (state, action) => {
        state.isRefreshingAccess = true;
      })
      .addCase(refreshTokenSignIn.rejected, (state, action) => {
        state.idToken = "";
        state.accessToken = "";
        state.sessionToken = "";
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("accessToken");
        state.isRefreshingAccess = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.idToken = "";
        state.accessToken = "";
        state.sessionToken = "";
        state.accessLevel = [];
        sessionStorage.removeItem("refreshToken");
        sessionStorage.removeItem("accessToken");
      });
  },
});

// export const { getBusinesses } = businessesSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`

export const selectIdToken = (state: RootState) => state.account.idToken;
export const selectAccessToken = (state: RootState) =>
  state.account.accessToken;
export const selectUserAccessLevel = (state: RootState) =>
  state.account.accessLevel;
export const selectCouplingPlatformAccessLevel = (state: RootState) =>
  state.account.couplingPlatformAccess;
export const selectIsKlearlyAdmin = (state: RootState) =>
  state.account.accessLevel?.some(
    (access) =>
      AccessLevel.KlearlyAdmin === access ||
      AccessLevel.KlearlySuperAdmin === access
  );
export const selectIsCoretechdEmployee = (state: RootState) =>
  state.account.accessLevel?.some(
    (access) => AccessLevel.CoreTechEmployee === access
  );
export const selectIsKlearlySuperAdmin = (state: RootState) =>
  state.account.accessLevel?.some(
    (access) => AccessLevel.KlearlySuperAdmin === access
  );
export const selectIsKlearlyAnalyticsAdmin = (state: RootState) =>
  state.account.accessLevel?.some(
    (access) => AccessLevel.KlearlyAnalytics === access
  );
export const selectIsKlearlySalesAdmin = (state: RootState) =>
  state.account.accessLevel?.some(
    (access) => AccessLevel.KlearlySalesAdmin === access
  );
export const selectIsKlearlySalesSuperAdmin = (state: RootState) =>
  state.account.accessLevel?.some(
    (access) =>
      AccessLevel.KlearlySalesSuperAdmin === access ||
      AccessLevel.KlearlySuperAdmin === access
  );
export const selectUserRole = (state: RootState) => {
  if (state.account.accessLevel?.includes(AccessLevel.KlearlySuperAdmin))
    return "Super Admin";
  if (state.account.accessLevel?.includes(AccessLevel.KlearlyAdmin))
    return "Admin";
  if (state.account.accessLevel?.includes(AccessLevel.CoreTechEmployee))
    return "CoreTech Partner";
  if (state.account.accessLevel?.includes(AccessLevel.Partner))
    return "Klearly Partner";
};
export const selectUser = (state: RootState) => state.account.user;
export const selectUserPartnerId = (state: RootState) =>
  state.account.user?.partnerId;
export const selectIsRefreshingAccess = (state: RootState) =>
  state.account.isRefreshingAccess;
export const selectSessionToken = (state: RootState) =>
  state.account.sessionToken;
export const selectMFASetupComplete = (state: RootState) =>
  state.account.MFASetupComplete;
export const selectMFASetupQrcode = (state: RootState) =>
  state.account.MFASetupQrcode;
export const selectMFASetupAccessToken = (state: RootState) =>
  state.account.MFASetupAccessToken;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
// export const incrementIfOdd =
//   (amount: number): AppThunk =>
//   (dispatch, getState) => {
//     const currentValue = selectCount(getState());
//     if (currentValue % 2 === 1) {
//       dispatch(incrementByAmount(amount));
//     }
//   };
export const { setIdTokenFrom401Request, resetUserState } =
  accountSlice.actions;

export default accountSlice.reducer;
